import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link as MuiLink,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EnergySavingsLeaf,
  ArrowForward,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext.tsx';
import { LoginRequest } from '../types';

const SIDEBAR_BG = '#0f172a';
const EMERALD    = '#10b981';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, loading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const { control, handleSubmit, formState: { errors } } = useForm<LoginRequest>({
    defaultValues: { email: '', password: '' },
  });

  useEffect(() => { clearError(); }, [clearError]);

  const onSubmit = async (data: LoginRequest) => {
    try {
      await login(data);
      navigate('/dashboard');
    } catch (_) {}
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* ── Left panel ─────────────────────────────────────────────── */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: 420,
        flexShrink: 0,
        bgcolor: SIDEBAR_BG,
        p: 5,
      }}>
        {/* Brand */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            bgcolor: EMERALD,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <EnergySavingsLeaf sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Zena
            </Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              CRM
            </Typography>
          </Box>
        </Stack>

        {/* Hero copy */}
        <Box>
          <Typography sx={{
            color: '#f8fafc', fontWeight: 700,
            fontSize: '1.875rem', lineHeight: 1.2, letterSpacing: '-0.03em', mb: 2,
          }}>
            Manage your renewable energy business — all in one place.
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.9375rem', lineHeight: 1.6 }}>
            DNO/MCS documentation, quote generation, specialised project management — built for UK heat pump installers.
          </Typography>
        </Box>

        {/* Footer */}
        <Typography sx={{ color: '#334155', fontSize: '0.75rem' }}>
          © {new Date().getFullYear()} Zena CRM
        </Typography>
      </Box>

      {/* ── Right panel (form) ──────────────────────────────────────── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#fafaf9',
        px: { xs: 2, sm: 4 },
        py: 6,
      }}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile brand */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4, display: { xs: 'flex', md: 'none' } }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: 1.5,
              bgcolor: EMERALD,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <EnergySavingsLeaf sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'text.primary', letterSpacing: '-0.02em' }}>
              Zena CRM
            </Typography>
          </Stack>

          <Typography variant="h4" sx={{ mb: 0.75, fontWeight: 700, letterSpacing: '-0.025em' }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, fontSize: '0.9375rem' }}>
            Sign in to your account to continue
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email address"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#ffffff',
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required', minLength: { value: 6, message: 'Min 6 characters' } }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    size="medium"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                            size="small"
                            sx={{ color: 'text.secondary' }}
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#ffffff',
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                endIcon={!loading && <ArrowForward sx={{ fontSize: 18 }} />}
                sx={{
                  mt: 0.5,
                  py: 1.375,
                  borderRadius: 2,
                  fontSize: '0.9375rem',
                  bgcolor: SIDEBAR_BG,
                  '&:hover': { bgcolor: '#1e293b' },
                }}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>

            <Box sx={{ textAlign: 'center', mt: 2.5 }}>
              <MuiLink component={Link} to="/register" variant="body2" sx={{ color: 'text.secondary' }}>
                Don't have an account?{' '}
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Sign up
                </Box>
              </MuiLink>
            </Box>
          </Box>

          {/* Demo credentials */}
          <Divider sx={{ my: 3.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Demo accounts
            </Typography>
          </Divider>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
